import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ImportSemiAutoStore, SemiautoImportMode } from '@usercommon/stores/import-semiauto.store';
import { List } from 'immutable';
import { NeqInformationPopinComponent } from '@sharedcommon/components/information-popin/information-popin.component';
import { MatDialog } from '@angular/material/dialog';
import {
  RawMaterialSemiAutoLineDto,
  SemiautoImportLineStatus
} from '@sharedcommon/rest/data.restservice';
import { Subscription } from 'rxjs';
import { ValidationService } from '@sharedcommon/services/validation.service';

/**
 * This component allows the display of the progress bar as well as the list of RMs used during a semi-automatic import.
 *
 * It contains the logic for displaying this component based on the progress of the import.
 */
@Component({
  selector: 'neq-import-semiauto',
  templateUrl: './import-semiauto.component.html',
  styleUrls: ['./import-semiauto.component.scss']
})
export class ImportSemiautoComponent implements OnInit, OnDestroy {

  processedFilename: string = '';
  items = [];
  isLastRm = false;
  // i18n key
  componentLabel: string = 'label.user.rawmaterial.header.import.semi-auto.creation';

  private currentRm: RawMaterialSemiAutoLineDto;
  private total: number = 0;
  private processed: number = 0;
  private rms = [];

  private lastRmSubscription: Subscription;
  private fileNameSubscription: Subscription;
  private currentRmSubscription: Subscription;
  private totalRmToCreateSubscription: Subscription;
  private numberOfProcessedRmSubscription: Subscription;
  private rmsToProcessSubscription: Subscription;

  constructor(
    private readonly importSemiAutoStore: ImportSemiAutoStore,
    private readonly router: Router,
    private readonly dialogRef: MatDialog,
    private readonly validationService: ValidationService
  ) {
  }

  ngOnInit(): void {
    this.initSemiAuto();
  }

  ngOnDestroy(): void {
    this.lastRmSubscription?.unsubscribe();
    this.fileNameSubscription?.unsubscribe();
    this.currentRmSubscription?.unsubscribe();
    this.totalRmToCreateSubscription?.unsubscribe();
    this.numberOfProcessedRmSubscription?.unsubscribe();
    this.rmsToProcessSubscription?.unsubscribe();
  }

  getProcessed(): number {
    return this.processed;
  }

  getTotal(): number {
    return this.total;
  }

  getRms() {
    return this.rms;
  }

  private initSemiAuto(): void {
    this.fileNameSubscription = this.importSemiAutoStore.getFileName()
      .subscribe((filename: string) => {
        this.processedFilename = filename;
        if (this.importSemiAutoStore.getImportMode() == SemiautoImportMode.CREATION) {
          this.componentLabel = 'label.user.rawmaterial.header.import.semi-auto.creation';
        } else {
          this.componentLabel = 'label.user.rawmaterial.header.import.semi-auto.modification';
        }
      });

    this.currentRmSubscription = this.importSemiAutoStore.getCurrentRm()
      .subscribe((rm: RawMaterialSemiAutoLineDto) => {
        this.currentRm = rm;
        if (rm != null) {
          // deactivate in selectionnable liste
          if (this.items) {
            const size = this.items.length;
            for (let _i = 0; _i < size; _i++) {
              const it = this.items[_i];
              if (rm.lineNumber == it.lineNumber) {
                it.disabled = true;
              }
            }
          }
        }
      });

    this.totalRmToCreateSubscription = this.importSemiAutoStore.getTotalToProcess()
      .subscribe((total: number) => {
        this.total = total;
      });

    this.numberOfProcessedRmSubscription = this.importSemiAutoStore.getNumerOfProcessedRm()
      .subscribe((processed: number) => {
        this.processed = processed;
      });

    this.rmsToProcessSubscription = this.importSemiAutoStore.getRmsToProcess()
      .subscribe((rms: List<RawMaterialSemiAutoLineDto>) => {
        if (rms == null) {
          this.rms = [];
          return;
        }

        if (rms.size > 0) {
          this.rms = rms.map((value: RawMaterialSemiAutoLineDto) => {
            return {
              code: value.rawMaterial.code,
              label: value.rawMaterial.label,
              entity: value.rawMaterial.entity == null ? '' : value.rawMaterial.entity.identifier,
              lineNumber: value.lineNumber,
              status: value.status
            };
          }).toArray();

          this.items = this.rms.map(r => {
            return {
              label: r.code + ' - ' + r.label,
              code: r.code,
              lineNumber: r.lineNumber,
              styleClass: this.getCssClassFromStatus(r.status),
              command: async (event) => {
                await this.onSelectSemiAutoRmtoImport(event.item)
              },
              disabled: this.currentRm != null && r.lineNumber == this.currentRm.lineNumber,
            };
          });
        }
      });

    this.lastRmSubscription = this.importSemiAutoStore.getLastRm()
      .subscribe((isLast: boolean) => {
        this.isLastRm = isLast
      });
  }

  async onClicStopProcess(): Promise<void> {
    // confirm before quit
    const callback = async () => {
      this.importSemiAutoStore.stopImport();
      await this.router.navigate(['/app/rawmaterials']);
    };
    this.validationService.checkModifications(callback);
  }

  semiAutoImportProgress(): number {
    return Math.ceil((this.processed * 100) / this.total);
  }

  semiAutoImportBuffer(): number {
    return (100 - this.semiAutoImportProgress());
  }

  getCssClassFromStatus(state: string) {
    switch (state) {
      case 'ok':
        return 'option-ok';
      case 'warning':
        return 'option-warning';
      case 'error':
        return 'option-error';
      default:
        return '';
    }
  }

  async onSelectSemiAutoRmtoImport(item) {
    // selected RM are disabled to avoid reusing it
    item.disabled = true;
    const rm: RawMaterialSemiAutoLineDto = this.importSemiAutoStore.getRmsToProcessValue()
      .find(rm => rm.lineNumber == item.lineNumber);

    if (rm != null && rm.status != SemiautoImportLineStatus.ERROR) {
      const callback = async () => {
        await this.importSemiAutoStore.processNextRm(rm);
      };
      this.validationService.checkModifications(callback);
    } else {
      this.dialogRef.open(NeqInformationPopinComponent,
        {
          data: {
            message: rm.errorMsg ?? 'label.user.rawmaterial.header.import.semi-auto.creation.error-rm',
            params: rm.errorMsg == null ? { code: rm.rawMaterial.code, error: rm.errorMsg } : { linenumber: rm.lineNumber }
          }
        });
    }
  }

  nextRm() {
    const callback = async () => {
      await this.importSemiAutoStore.nextRmToCreate();
    };
    this.validationService.checkModifications(callback);
  }
}
